import {DSVRowArray, DSVRowString} from "d3";
import {BehaviorSubject, distinctUntilChanged, map, Observable} from "rxjs";
import {DashboardFilterUISettings} from "./dashboard_filter_ui_settings";
import {DashboardFilterApplicator} from "./dashboard_filter_applicators";
import {ParentFilterSettings} from "./dashboard_filter_parent_filter_settings";

export interface DashboardFilterInterface<V, US, O, PFV> {
  name: string;
  uiSettings: US;
  applicator?: DashboardFilterApplicator<any, DSVRowArray, DSVRowString>;
  parentFilterSettings?: ParentFilterSettings<O, PFV>;
  initialValue: V;
  isEnabled?: boolean;
  ignoreProductSetting?: boolean;
}

export abstract class DashboardFilter<FV, US extends DashboardFilterUISettings = DashboardFilterUISettings, O = any, PFV = any> {
  readonly name: string;
  readonly uiSettings: US;
  readonly applicator?: DashboardFilterApplicator<FV, DSVRowArray, DSVRowString>;
  readonly parentFilterSettings?: ParentFilterSettings<O, PFV>;
  protected readonly _value$: Observable<FV>;
  private _valueSubject: BehaviorSubject<FV>;

  protected readonly _isEnabled$: Observable<boolean>;
  private _isEnabledSubject: BehaviorSubject<boolean>;

  readonly ignoreProductSetting?: boolean

  protected constructor(args: DashboardFilterInterface<FV, US, O, PFV>) {
    this.name = args.name;
    this.uiSettings = args.uiSettings;
    this.applicator = args.applicator;
    this.parentFilterSettings = args.parentFilterSettings;

    this._valueSubject = new BehaviorSubject<FV>(args.initialValue);
    this._value$ = this._getValueSubjectObservable();

    this._isEnabledSubject = new BehaviorSubject<boolean>(args.isEnabled ?? true);
    this._isEnabled$ = this._isEnabledSubject.asObservable();

    this.ignoreProductSetting = args.ignoreProductSetting
  }

  private _parentFilter?: DashboardFilter<PFV>;

  get parentFilter(): DashboardFilter<PFV> | undefined {
    return this._parentFilter;
  }

  private _childFilter?: DashboardFilter<PFV>

  get childFilter(): DashboardFilter<PFV> | undefined {
    return this._childFilter
  }

  get value(): FV {
    return this._valueSubject.value;
  }

  get isEnabled(): boolean {
    return this._isEnabledSubject.value;
  }

  set isEnabled(isEnabled: boolean) {
    this._isEnabledSubject.next(isEnabled);
  }

  get isEnabled$(): Observable<boolean> {
    return this._isEnabled$;
  }

  setIsEnabled(isEnabled: boolean) {
    this.isEnabled = isEnabled;
  }

  set value(value: FV | undefined | null) {
    this.setValue(value);
  }

  setValue(value: FV | undefined | null) {
    let valueParsed = value == null ? this._parseNullValue(value) : value;

    if (!this.isValueValid(valueParsed)) {
      valueParsed = this.handleInvalidValue(valueParsed);
    }

    if (valueParsed === this.value) {
      return;
    }

    this.setValueNoChecks(valueParsed);
  }

  protected setValueNoChecks(value: FV) {
    if (value === this.value) {
      return;
    }
    this._valueSubject.next(value);
  }

  protected handleInvalidValue(value: FV): FV {
    throw Error(`Value ${value} is not valid for filter ${this.name}`);
  }

  get value$(): Observable<FV> {
    return this._value$;
  }

  _parseNullValue(value: undefined | null | FV): FV {
    throw Error('Not implemented');
  }

  _getValueSubjectObservable(): Observable<FV> {
    return this._valueSubject.pipe(
      distinctUntilChanged(),
      map((v) => {
        return v;
      }),
      // map(value => this.filterValidValue(value)),
      // distinctUntilChanged()
    );
  }

  setParentFilter(parentFilter: DashboardFilter<PFV>) {
    this._parentFilter = parentFilter;
  }

  setChildFilter(filter: DashboardFilter<any>) {
    this._childFilter = filter
  }

  rowPassesFilter(row: DSVRowString): boolean {
    return this.applicator?.rowPassesFilter(row, this.value) ?? true;
  }

  applyFilter(data: DSVRowArray): DSVRowArray {
    return this.applicator?.applyFilter(data, this.value) ?? data;
  }

  dataRefreshFromRemoteNecessary(previousFilterValue: FV | undefined): boolean {
    return this.applicator?.dataRefreshFromRemoteNecessary(this.value, previousFilterValue) ?? false;
  }


  initialize() {
  }

  filterValidValue(value: FV): FV {
    return value;
  }

  abstract isValueValid(value: any): boolean;
}
